// src/pages/PipelineFormPage.jsx
// Formulaire de création / édition d'un profil dans le Pipe candidats
// Remplace CandidateFormPage — crée directement un CandidatePipeline
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { pipelineAPI } from '../services/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, UserCheck } from 'lucide-react';

const PRIORITIES = ['HAUTE', 'NORMALE', 'BASSE'];
const STATUSES   = ['NOUVEAU','EN_COURS','CONTACTE','ENTRETIEN','PROPOSE','PLACE','ARCHIVE','REFUSE'];

export default function PipelineFormPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isEdit   = !!id;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name:'', title:'', email:'', phone:'', location:'',
    linkedin_link:'', profile_url:'', cv_link:'', availability:'', tjm:'', experience:'',
    priority:'NORMALE', pipeline_status:'NOUVEAU',
    internal_comment:'', client_feedback:'',
    skills: [],
  });

  useEffect(() => {
    if (!isEdit) return;
    pipelineAPI.getOne(id)
      .then(r => setForm(r.data))
      .catch(() => { toast.error('Profil introuvable'); navigate('/pipeline'); });
  }, [id]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.full_name) { toast.error('Nom complet obligatoire'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await pipelineAPI.update(id, form);
        toast.success('Profil mis à jour');
        navigate(`/pipeline/${id}`);
      } else {
        const res = await pipelineAPI.create({ ...form, source_type: 'MANUEL' });
        toast.success('Profil ajouté au pipeline');
        navigate(`/pipeline/${res.data.id}`);
      }
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left" style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button className="btn btn-ghost btn-icon"
            onClick={() => navigate(isEdit ? `/pipeline/${id}` : '/pipeline')}>
            <ArrowLeft size={18}/>
          </button>
          <div>
            <h1>{isEdit ? 'Modifier le profil' : 'Nouveau profil — Pipe candidats'}</h1>
            <p>{isEdit ? form.full_name : 'Ajout manuel d\'un candidat dans le pipeline'}</p>
          </div>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary"
            onClick={() => navigate(isEdit ? `/pipeline/${id}` : '/pipeline')}>
            Annuler
          </button>
          <button className="btn btn-primary" disabled={saving} onClick={handleSubmit}>
            {saving ? <div className="spinner" style={{ width:14, height:14 }}/> : <Save size={14}/>}
            {isEdit ? ' Mettre à jour' : ' Ajouter au pipe'}
          </button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>

        {/* Identité */}
        <div className="card">
          <div className="card-header">
            <span className="card-title"><UserCheck size={14} style={{ verticalAlign:'middle', marginRight:6 }}/>Identité</span>
          </div>
          <div style={{ display:'grid', gap:12 }}>
            <F label="Nom complet *"    value={form.full_name}    onChange={set('full_name')}    placeholder="Prénom Nom"/>
            <F label="Titre / Poste"    value={form.title}        onChange={set('title')}        placeholder="Développeur Java Senior"/>
            <F label="Expérience"       value={form.experience}   onChange={set('experience')}   placeholder="5 ans"/>
            <F label="Localisation"     value={form.location}     onChange={set('location')}     placeholder="Paris, Île-de-France"/>
          </div>
        </div>

        {/* Contact */}
        <div className="card">
          <div className="card-header"><span className="card-title">Contact</span></div>
          <div style={{ display:'grid', gap:12 }}>
            <F label="Email"     type="email" value={form.email}         onChange={set('email')}/>
            <F label="Téléphone"             value={form.phone}         onChange={set('phone')}/>
            <F label="LinkedIn"              value={form.linkedin_link} onChange={set('linkedin_link')} placeholder="https://linkedin.com/in/…"/>
            <F label="Lien Profil"           value={form.profile_url}   onChange={set('profile_url')}   placeholder="https://…"/>
            <F label="Lien CV"               value={form.cv_link}       onChange={set('cv_link')}       placeholder="https://…"/>
          </div>
        </div>

        {/* Disponibilité */}
        <div className="card">
          <div className="card-header"><span className="card-title">Disponibilité & TJM</span></div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <F label="Disponibilité" value={form.availability} onChange={set('availability')} placeholder="Immédiate, 1 mois…"/>
            <F label="TJM"           value={form.tjm}          onChange={set('tjm')}          placeholder="600€/j"/>
          </div>
        </div>

        {/* Statut pipe */}
        <div className="card">
          <div className="card-header"><span className="card-title">Statut dans le pipe</span></div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <F label="Statut"   type="select" value={form.pipeline_status} onChange={set('pipeline_status')} options={STATUSES}/>
            <F label="Priorité" type="select" value={form.priority}        onChange={set('priority')}        options={PRIORITIES}/>
          </div>
        </div>

        {/* Notes */}
        <div className="card" style={{ gridColumn:'1/-1' }}>
          <div className="card-header"><span className="card-title">Notes & Retours</span></div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <F label="Commentaire interne" type="textarea" value={form.internal_comment} onChange={set('internal_comment')} placeholder="Contexte, infos internes…"/>
            <F label="Retour client"       type="textarea" value={form.client_feedback}  onChange={set('client_feedback')}  placeholder="Feedback du client…"/>
          </div>
        </div>
      </div>
    </div>
  );
}

function F({ label, type='text', value, onChange, placeholder='', options=[] }) {
  const s = { width:'100%', padding:'9px 12px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', outline:'none', background:'var(--bg-elevated)', color:'var(--text-primary)' };
  const l = { display:'block', fontSize:'0.8125rem', fontWeight:600, color:'var(--text-muted)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' };
  return (
    <div>
      <label style={l}>{label}</label>
      {type === 'select' ? (
        <select style={s} value={value||''} onChange={onChange}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea style={{ ...s, minHeight:80, resize:'vertical' }} value={value||''} onChange={onChange} placeholder={placeholder}/>
      ) : (
        <input style={s} type={type} value={value||''} onChange={onChange} placeholder={placeholder}/>
      )}
    </div>
  );
}
